import React, { memo } from 'react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { FolderOpen, FileText, Upload, Eye, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

// Memoized File Card
const FileCard = memo(({ attachment }) => {
  const isImage = attachment.file_type?.startsWith('image');
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-3">
        <div className="aspect-square bg-slate-100 dark:bg-slate-800 rounded-lg flex items-center justify-center mb-2 overflow-hidden">
          {isImage && attachment.file_url ? (
            <img src={attachment.file_url} alt={attachment.description || 'Attachment'} className="object-cover w-full h-full" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              {attachment.file_type?.includes('pdf') ? (
                <FileText className="h-10 w-10 text-red-500" />
              ) : attachment.file_type?.includes('spreadsheet') || attachment.file_type?.includes('excel') ? (
                <FileText className="h-10 w-10 text-green-500" />
              ) : attachment.file_type?.includes('document') || attachment.file_type?.includes('word') ? (
                <FileText className="h-10 w-10 text-blue-500" />
              ) : (
                <FileText className="h-10 w-10" />
              )}
              <span className="text-xs">{attachment.file_type?.split('/')[1]?.toUpperCase() || 'FILE'}</span>
            </div>
          )}
        </div>
        <p className="text-sm font-medium truncate">{attachment.description || attachment.filename || 'Unnamed file'}</p>
        <p className="text-xs text-muted-foreground">
          {attachment.created_at ? formatDistanceToNow(new Date(attachment.created_at), { addSuffix: true }) : 'Unknown date'}
        </p>
        <div className="flex gap-2 mt-2">
          <Button variant="ghost" size="sm" className="flex-1" onClick={() => window.open(attachment.file_url, '_blank')}>
            <Eye className="h-3 w-3 mr-1" />
            View
          </Button>
          <Button variant="ghost" size="sm" className="flex-1" onClick={() => window.open(attachment.file_url, '_blank')}>
            <ExternalLink className="h-3 w-3 mr-1" />
            Download
          </Button>
        </div>
      </CardContent>
    </Card>
  );
});

FileCard.displayName = 'FileCard';

// Main FilesTab component
const FilesTab = memo(({ attachments, onUploadClick }) => {
  return (
    <ScrollArea className="h-[600px]">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600" />
              Files & Attachments
            </h3>
            <p className="text-sm text-muted-foreground">
              All files shared with this contact
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={onUploadClick}>
            <Upload className="h-4 w-4 mr-2" />
            Upload File
          </Button>
        </div>

        {attachments.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {attachments.map((att, idx) => (
              <FileCard key={idx} attachment={att} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No files attached to this contact</p>
            <p className="text-sm mt-1">Upload files to share them with this contact</p>
          </div>
        )}
      </div>
    </ScrollArea>
  );
});

FilesTab.displayName = 'FilesTab';

export default FilesTab;
